// src/pages/Consultation/StartSessionModal.jsx - ENHANCED with Multi-Parameter Search

/**
 * ENHANCED APPOINTMENT SEARCH FEATURES:
 *
 * 1. Multiple Search Parameters:
 *    - userPhone=9876543210
 *    - userName=Rahul
 *    - appointmentCode=APT-123
 *    - status=CONFIRMED
 *    - date=2025-10-31
 *
 * 2. Smart Query Parsing:
 *    - Detects parameter format (key=value)
 *    - Falls back to smart single search
 *
 * 3. Combined Search:
 *    - userPhone=987&userName=Rahul
 *    - Multiple filters at once
 */

import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import {
  FiX,
  FiCalendar,
  FiUser,
  FiSearch,
  FiClock,
  FiPhone,
  FiMail,
  FiEdit2,
  FiFilter,
} from "react-icons/fi";
import { startSessionThunk } from "../../store/thunks/consultationThunks";
import {
  getAppointmentDetailsThunk,
  getAppointmentsThunk,
  searchAppointmentsThunk,
} from "../../store/thunks/appointmentThunks";
import Button from "../../components/common/Button";
import Badge from "../../components/common/Badge";
import toast from "react-hot-toast";
import UserSelection from "pages/User/components/UserSelection";

const StartSessionModal = ({ onClose, onSuccess }) => {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const searchInputRef = useRef(null);

  const { user } = useSelector((state) => state.auth);
  const { selectedBranch } = useSelector((state) => state.rate.ui);
  const { loading } = useSelector((state) => state.consultation);

  // Session type selection
  const [sessionType, setSessionType] = useState(null);

  // Form data
  const [formData, setFormData] = useState({
    sessionType: "",
    appointmentId: "",
    userId: "",
    guestCustomer: {
      name: "",
      phoneNumber: "",
      email: "",
    },
    branchCode: selectedBranch || user?.branchAccess?.primaryBranch || "",
    storeCode: "",
    adminCode: user?.adminCode || user?._id || "",
    adminId: user?._id || "",
    adminName: user?.name || "",
    adminRole: user?.role || "",
    expectedEndTime: "",
    source: "ADMIN_DASHBOARD",
    channel: "IN_STORE",
  });

  // User selection state
  const [showUserSelection, setShowUserSelection] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // ✅ ENHANCED: Appointment selection state with structured search
  const [showAppointmentSelector, setShowAppointmentSelector] = useState(false);
  const [appointmentSearch, setAppointmentSearch] = useState("");
  const [appointmentSearchParams, setAppointmentSearchParams] = useState({}); // ✅ NEW: Structured params
  const [appointments, setAppointments] = useState([]);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [appointmentLoading, setAppointmentLoading] = useState(false);
  const [appointmentView, setAppointmentView] = useState("search");
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false); // ✅ NEW: Toggle advanced

  // Initialize from query parameters
  useEffect(() => {
    const appointmentId = searchParams.get("appointmentId");
    const userId = searchParams.get("userId");

    if (appointmentId) {
      setSessionType("APPOINTMENT");
      loadAppointmentDetails(appointmentId);
    } else if (userId) {
      setSessionType("WALK_IN");
      setFormData((prev) => ({ ...prev, userId }));
    }
  }, [searchParams]);

  // ✅ NEW: Parse search query for parameters
  const parseSearchQuery = (query) => {
    // Check if query contains parameter format (key=value)
    const paramPattern = /(\w+)=([^&]+)/g;
    const matches = [...query.matchAll(paramPattern)];

    if (matches.length > 0) {
      // Multi-parameter search
      const params = {};
      matches.forEach((match) => {
        const [, key, value] = match;
        params[key] = value.trim();
      });
      return { type: "structured", params };
    }

    // Single smart search
    if (query.length >= 2) {
      // Detect what type of search it is
      const phonePattern = /^\d{10}$/;
      const emailPattern = /@/;
      const codePattern = /^APT-/i;

      if (phonePattern.test(query.replace(/\D/g, ""))) {
        return {
          type: "structured",
          params: { userPhone: query.replace(/\D/g, "") },
        };
      } else if (emailPattern.test(query)) {
        return { type: "structured", params: { search: query } };
      } else if (codePattern.test(query)) {
        return { type: "structured", params: { appointmentCode: query } };
      } else {
        return { type: "structured", params: { userName: query } };
      }
    }

    return { type: "none", params: {} };
  };

  // Load appointment details
  const loadAppointmentDetails = async (appointmentId) => {
    try {
      setAppointmentLoading(true);
      const result = await dispatch(
        getAppointmentDetailsThunk(appointmentId)
      ).unwrap();

      if (result?.appointment) {
        handleAppointmentSelect(result.appointment);
      }
    } catch (error) {
      console.error("Error loading appointment:", error);
      toast.error("Failed to load appointment details");
    } finally {
      setAppointmentLoading(false);
    }
  };

  // ✅ ENHANCED: Load appointments with structured parameters
  const loadAppointments = async (
    view = appointmentView,
    customParams = null
  ) => {
    try {
      setAppointmentLoading(true);
      let result;
      let searchFilters = customParams || appointmentSearchParams;

      if (view === "search") {
        // Use structured search parameters
        if (
          Object.keys(searchFilters).length > 0 ||
          appointmentSearch.length >= 2
        ) {
          // Parse the query if not already parsed
          if (!customParams && appointmentSearch) {
            const parsed = parseSearchQuery(appointmentSearch);
            searchFilters = parsed.params;
            setAppointmentSearchParams(searchFilters);
          }

          console.log("🔍 Searching appointments with filters:", searchFilters);

          result = await dispatch(
            getAppointmentsThunk({
              ...searchFilters,
              branchCode: selectedBranch,
              status: ["CONFIRMED", "PENDING", "CHECKED_IN"],
            })
          ).unwrap();
        }
      } else if (view === "assigned") {
        result = await dispatch(
          getAppointmentsThunk({
            adminCode: user?.adminCode || user?._id,
            status: ["CONFIRMED", "PENDING", "CHECKED_IN"],
            branchCode: selectedBranch,
          })
        ).unwrap();
      } else if (view === "today") {
        const today = new Date().toISOString().split("T")[0];
        result = await dispatch(
          getAppointmentsThunk({
            date: today,
            branchCode: selectedBranch,
            status: ["CONFIRMED", "PENDING", "CHECKED_IN"],
          })
        ).unwrap();
      }

      setAppointments(result?.appointments || []);
    } catch (error) {
      console.error("Error loading appointments:", error);
      toast.error("Failed to load appointments");
      setAppointments([]);
    } finally {
      setAppointmentLoading(false);
    }
  };

  // ✅ ENHANCED: Search with debouncing and parameter parsing
  useEffect(() => {
    if (appointmentView === "search" && appointmentSearch.length >= 2) {
      const timer = setTimeout(() => {
        const parsed = parseSearchQuery(appointmentSearch);
        setAppointmentSearchParams(parsed.params);
        loadAppointments("search", parsed.params);
      }, 300);
      return () => clearTimeout(timer);
    } else if (appointmentSearch.length === 0) {
      setAppointmentSearchParams({});
      setAppointments([]);
    }
  }, [appointmentSearch]);

  // Load appointments when view changes
  useEffect(() => {
    if (showAppointmentSelector && appointmentView !== "search") {
      loadAppointments(appointmentView);
    }
  }, [appointmentView, showAppointmentSelector]);

  // ✅ NEW: Handle advanced search form changes
  const handleAdvancedSearchChange = (field, value) => {
    setAppointmentSearchParams((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // ✅ NEW: Apply advanced search
  const handleApplyAdvancedSearch = () => {
    // Remove empty values
    const cleanParams = Object.fromEntries(
      Object.entries(appointmentSearchParams).filter(
        ([_, value]) => value !== ""
      )
    );

    console.log("🔍 Advanced search params:", cleanParams);
    loadAppointments("search", cleanParams);
  };

  // ✅ NEW: Clear advanced search
  const handleClearAdvancedSearch = () => {
    setAppointmentSearchParams({});
    setAppointmentSearch("");
    setAppointments([]);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name.startsWith("guestCustomer.")) {
      const field = name.split(".")[1];
      setFormData((prev) => ({
        ...prev,
        guestCustomer: {
          ...prev.guestCustomer,
          [field]: value,
        },
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  // Handle appointment selection
  const handleAppointmentSelect = (appointment) => {
    setSelectedAppointment(appointment);
    setFormData((prev) => ({
      ...prev,
      appointmentId: appointment._id,
      userId: appointment.userId,
      guestCustomer: {
        name: appointment.userName || "",
        phoneNumber: appointment.userPhone || "",
        email: appointment.userEmail || "",
      },
    }));
    setShowAppointmentSelector(false);
    toast.success(`Selected appointment for ${appointment.userName}`);
  };

  // Handle user selection
  const handleUserSelected = (user) => {
    console.log("User selected:", user);
    setSelectedUser(user);

    setFormData((prev) => ({
      ...prev,
      userId: user._id || user.customerId,
      guestCustomer: {
        name: user.name || "",
        phoneNumber: user.phoneNumber || "",
        email: user.email || "",
      },
    }));

    setShowUserSelection(false);
    toast.success(`Selected customer: ${user.name}`);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!sessionType) {
      toast.error("Please select a session type");
      return;
    }

    if (sessionType === "WALK_IN") {
      if (
        !formData.userId &&
        (!formData.guestCustomer.name || !formData.guestCustomer.phoneNumber)
      ) {
        toast.error("Please select a customer or provide guest details");
        return;
      }
    }

    if (sessionType === "APPOINTMENT" && !formData.appointmentId) {
      toast.error("Please select an appointment");
      return;
    }

    try {
      const sessionData = {
        ...formData,
        sessionType,
      };

      const result = await dispatch(startSessionThunk(sessionData)).unwrap();

      if (result?.data?.session) {
        toast.success("Session started successfully");
        onSuccess(result.data.session);
      } else {
        throw new Error("Invalid response format");
      }
    } catch (error) {
      console.error("Start session error:", error);
      toast.error(error || "Failed to start session");
    }
  };

  // Format date/time
  const formatDateTime = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleString("en-IN", {
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Get status color
  const getStatusColor = (status) => {
    const colors = {
      PENDING: "warning",
      CONFIRMED: "success",
      CHECKED_IN: "info",
    };
    return colors[status] || "default";
  };

  return (
    <>
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-40"
        onClick={onClose}
      />

      <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-white rounded-lg shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">Start New Session</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <FiX size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="p-6 space-y-6">
            {/* Session Type Selection */}
            {!sessionType && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Select Session Type *
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    type="button"
                    onClick={() => {
                      setSessionType("APPOINTMENT");
                      setShowAppointmentSelector(true);
                    }}
                    className="p-6 border-2 border-gray-200 rounded-lg hover:border-primary hover:bg-primary/5 transition-all text-center"
                  >
                    <FiCalendar className="mx-auto text-3xl text-primary mb-3" />
                    <h3 className="font-medium text-gray-900 mb-1">
                      From Appointment
                    </h3>
                    <p className="text-sm text-gray-500">
                      Start session from scheduled appointment
                    </p>
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setSessionType("WALK_IN");
                      setShowUserSelection(true);
                    }}
                    className="p-6 border-2 border-gray-200 rounded-lg hover:border-primary hover:bg-primary/5 transition-all text-center"
                  >
                    <FiUser className="mx-auto text-3xl text-primary mb-3" />
                    <h3 className="font-medium text-gray-900 mb-1">
                      Walk-in Customer
                    </h3>
                    <p className="text-sm text-gray-500">
                      Start session for walk-in customer
                    </p>
                  </button>
                </div>
              </div>
            )}

            {/* Appointment Flow */}
            {sessionType === "APPOINTMENT" && (
              <div>
                <button
                  type="button"
                  onClick={() => {
                    setSessionType(null);
                    setSelectedAppointment(null);
                    setFormData((prev) => ({
                      ...prev,
                      appointmentId: "",
                      userId: "",
                    }));
                  }}
                  className="text-sm text-primary hover:underline mb-4"
                >
                  ← Change session type
                </button>

                {/* Selected Appointment Display */}
                {selectedAppointment ? (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <FiCalendar className="text-green-600" />
                          <span className="font-medium text-green-900">
                            Selected Appointment
                          </span>
                          <Badge
                            variant={getStatusColor(selectedAppointment.status)}
                            size="sm"
                          >
                            {selectedAppointment.status}
                          </Badge>
                        </div>

                        <div className="space-y-2">
                          <div className="flex items-center gap-2 text-sm">
                            <FiUser className="text-gray-500" />
                            <span className="font-medium">
                              {selectedAppointment.userName}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <FiPhone className="text-gray-500" />
                            {selectedAppointment.userPhone}
                          </div>
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <FiClock className="text-gray-500" />
                            {formatDateTime(
                              selectedAppointment.scheduledDateTime
                            )}
                          </div>
                          <div className="text-sm text-gray-600">
                            <span className="font-medium">Type:</span>{" "}
                            {selectedAppointment.type?.replace(/_/g, " ")}
                          </div>
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={() => setShowAppointmentSelector(true)}
                        className="text-sm text-primary hover:underline"
                      >
                        Change
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
                    <FiCalendar className="mx-auto text-4xl text-gray-400 mb-3" />
                    <p className="text-gray-500 mb-4">
                      No appointment selected
                    </p>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowAppointmentSelector(true)}
                    >
                      <FiSearch className="mr-2" />
                      Select Appointment
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Walk-in Flow */}
            {sessionType === "WALK_IN" && (
              <div>
                <button
                  type="button"
                  onClick={() => {
                    setSessionType(null);
                    setSelectedUser(null);
                    setFormData((prev) => ({
                      ...prev,
                      userId: "",
                      guestCustomer: { name: "", phoneNumber: "", email: "" },
                    }));
                  }}
                  className="text-sm text-primary hover:underline mb-4"
                >
                  ← Change session type
                </button>

                {/* Selected User Display */}
                {selectedUser ? (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <FiUser className="text-green-600" />
                          <span className="font-medium text-green-900">
                            Selected Customer
                          </span>
                          {selectedUser.status === "accepted" && (
                            <Badge variant="success" size="sm">
                              Active
                            </Badge>
                          )}
                        </div>

                        <div className="space-y-2">
                          <div className="flex items-center gap-2 text-sm">
                            <FiUser className="text-gray-500" />
                            <span className="font-medium">
                              {selectedUser.name}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <FiPhone className="text-gray-500" />
                            {selectedUser.phoneNumber}
                          </div>
                          {selectedUser.email && (
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                              <FiMail className="text-gray-500" />
                              {selectedUser.email}
                            </div>
                          )}
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={() => setShowUserSelection(true)}
                        className="text-sm text-primary hover:underline flex items-center gap-1"
                      >
                        <FiEdit2 size={14} />
                        Change
                      </button>
                    </div>
                  </div>
                ) : formData.guestCustomer.name ? (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <FiUser className="text-blue-600" />
                          <span className="font-medium text-blue-900">
                            Guest Customer
                          </span>
                        </div>

                        <div className="space-y-2">
                          <div className="text-sm">
                            <span className="font-medium">
                              {formData.guestCustomer.name}
                            </span>
                          </div>
                          <div className="text-sm text-gray-600">
                            {formData.guestCustomer.phoneNumber}
                          </div>
                          {formData.guestCustomer.email && (
                            <div className="text-sm text-gray-600">
                              {formData.guestCustomer.email}
                            </div>
                          )}
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={() => setShowUserSelection(true)}
                        className="text-sm text-primary hover:underline flex items-center gap-1"
                      >
                        <FiEdit2 size={14} />
                        Change
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
                    <FiUser className="mx-auto text-4xl text-gray-400 mb-3" />
                    <p className="text-gray-500 mb-4">No customer selected</p>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowUserSelection(true)}
                    >
                      <FiSearch className="mr-2" />
                      Select Customer
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Common Fields */}
            {sessionType && (
              <div className="border-t pt-4">
                <h3 className="font-medium text-gray-900 mb-4">
                  Session Details
                </h3>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Branch *
                    </label>
                    <input
                      type="text"
                      name="branchCode"
                      value={formData.branchCode}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                      readOnly
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Expected Duration
                    </label>
                    <select
                      name="expectedDuration"
                      value={formData.expectedDuration}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                      <option value="">Select duration</option>
                      <option value="15">15 minutes</option>
                      <option value="30">30 minutes</option>
                      <option value="60">1 hour</option>
                      <option value="120">2 hours</option>
                    </select>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading || !sessionType}>
              {loading ? "Starting..." : "Start Session"}
            </Button>
          </div>
        </form>
      </div>

      {/* Appointment Selector Modal */}
      {showAppointmentSelector && (
        <AppointmentSelectorModal
          isOpen={showAppointmentSelector}
          onClose={() => setShowAppointmentSelector(false)}
          onSelect={handleAppointmentSelect}
          appointments={appointments}
          loading={appointmentLoading}
          searchQuery={appointmentSearch}
          onSearchChange={setAppointmentSearch}
          searchParams={appointmentSearchParams}
          onSearchParamsChange={handleAdvancedSearchChange}
          onApplyAdvancedSearch={handleApplyAdvancedSearch}
          onClearAdvancedSearch={handleClearAdvancedSearch}
          showAdvancedSearch={showAdvancedSearch}
          onToggleAdvancedSearch={() =>
            setShowAdvancedSearch(!showAdvancedSearch)
          }
          view={appointmentView}
          onViewChange={setAppointmentView}
          formatDateTime={formatDateTime}
          getStatusColor={getStatusColor}
          searchInputRef={searchInputRef}
        />
      )}

      {/* User Selection Modal */}
      {showUserSelection && (
        <UserSelection
          isOpen={showUserSelection}
          onClose={() => setShowUserSelection(false)}
          onUserSelected={handleUserSelected}
          mode="smart"
          title="Select Customer for Walk-in Session"
          showEmail={true}
          showAlternatePhone={false}
          showAddress={false}
          showKYC={false}
          showGSTIN={false}
          allowSearch={true}
          allowCreate={true}
          allowEdit={false}
          autoFillFromSearch={true}
        />
      )}
    </>
  );
};

// ✅ ENHANCED: Appointment Selector Modal with Multi-Parameter Search
const AppointmentSelectorModal = ({
  isOpen,
  onClose,
  onSelect,
  appointments,
  loading,
  searchQuery,
  onSearchChange,
  searchParams,
  onSearchParamsChange,
  onApplyAdvancedSearch,
  onClearAdvancedSearch,
  showAdvancedSearch,
  onToggleAdvancedSearch,
  view,
  onViewChange,
  formatDateTime,
  getStatusColor,
  searchInputRef,
}) => {
  if (!isOpen) return null;

  return (
    <>
      <div
        className="fixed inset-0 bg-black bg-opacity-60 z-50"
        onClick={onClose}
      />

      <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-[60] w-full max-w-3xl max-h-[85vh] bg-white rounded-lg shadow-2xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-gray-900">
              Select Appointment
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <FiX size={20} />
            </button>
          </div>

          {/* View Tabs */}
          <div className="flex gap-2 border-b border-gray-200">
            <button
              onClick={() => onViewChange("search")}
              className={`px-4 py-2 text-sm font-medium ${
                view === "search"
                  ? "text-primary border-b-2 border-primary"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              <FiSearch className="inline mr-1" />
              Search
            </button>
            <button
              onClick={() => onViewChange("assigned")}
              className={`px-4 py-2 text-sm font-medium ${
                view === "assigned"
                  ? "text-primary border-b-2 border-primary"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              <FiUser className="inline mr-1" />
              My Appointments
            </button>
            <button
              onClick={() => onViewChange("today")}
              className={`px-4 py-2 text-sm font-medium ${
                view === "today"
                  ? "text-primary border-b-2 border-primary"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              <FiCalendar className="inline mr-1" />
              Today
            </button>
          </div>
        </div>

        {/* ✅ ENHANCED: Search Section with Name and Phone Fields */}
        {view === "search" && (
          <div className="border-b border-gray-200">
            {/* Quick Search */}
            <div className="p-4">
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Customer Name
                  </label>
                  <div className="relative">
                    <FiUser className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      ref={searchInputRef}
                      type="text"
                      value={searchParams.userName || ""}
                      onChange={(e) => {
                        onSearchParamsChange("userName", e.target.value);
                        onSearchChange(e.target.value);
                      }}
                      placeholder="Enter name"
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                      autoFocus
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Phone Number
                  </label>
                  <div className="relative">
                    <FiPhone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      value={searchParams.userPhone || ""}
                      onChange={(e) => {
                        onSearchParamsChange("userPhone", e.target.value);
                        onSearchChange(e.target.value);
                      }}
                      placeholder="10-digit number"
                      maxLength="10"
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-between items-center">
                <button
                  onClick={onToggleAdvancedSearch}
                  className="text-sm text-primary hover:underline flex items-center gap-1"
                >
                  <FiFilter size={14} />
                  {showAdvancedSearch ? "Hide" : "Show"} more filters
                </button>

                {(searchParams.userName || searchParams.userPhone) && (
                  <button
                    onClick={onClearAdvancedSearch}
                    className="text-sm text-gray-500 hover:text-gray-700"
                  >
                    Clear search
                  </button>
                )}
              </div>
            </div>

            {/* ✅ NEW: Advanced Search Form */}
            {showAdvancedSearch && (
              <div className="px-4 pb-4 bg-gray-50">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Appointment Code
                    </label>
                    <input
                      type="text"
                      value={searchParams.appointmentCode || ""}
                      onChange={(e) =>
                        onSearchParamsChange("appointmentCode", e.target.value)
                      }
                      placeholder="APT-XXX"
                      className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Date
                    </label>
                    <input
                      type="date"
                      value={searchParams.date || ""}
                      onChange={(e) =>
                        onSearchParamsChange("date", e.target.value)
                      }
                      className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Status
                    </label>
                    <select
                      value={searchParams.status || ""}
                      onChange={(e) =>
                        onSearchParamsChange("status", e.target.value)
                      }
                      className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                      <option value="">All</option>
                      <option value="CONFIRMED">Confirmed</option>
                      <option value="PENDING">Pending</option>
                      <option value="CHECKED_IN">Checked In</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Type
                    </label>
                    <select
                      value={searchParams.type || ""}
                      onChange={(e) =>
                        onSearchParamsChange("type", e.target.value)
                      }
                      className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                      <option value="">All</option>
                      <option value="PRODUCT_CONSULTATION">
                        Product Consultation
                      </option>
                      <option value="CUSTOM_DESIGN">Custom Design</option>
                      <option value="WEDDING_JEWELRY">Wedding Jewelry</option>
                    </select>
                  </div>
                </div>

                <div className="flex justify-end gap-2 mt-3">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={onClearAdvancedSearch}
                  >
                    Clear All
                  </Button>
                  <Button size="sm" onClick={onApplyAdvancedSearch}>
                    Apply Filters
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Appointments List */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : appointments.length > 0 ? (
            <div className="space-y-3">
              {appointments.map((appointment) => (
                <AppointmentCard
                  key={appointment._id}
                  appointment={appointment}
                  onSelect={() => onSelect(appointment)}
                  formatDateTime={formatDateTime}
                  getStatusColor={getStatusColor}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <FiCalendar className="mx-auto text-4xl text-gray-300 mb-3" />
              <p className="text-gray-500 mb-2">
                {view === "search" &&
                !searchParams.userName &&
                !searchParams.userPhone
                  ? "Enter name or phoneNumber number to search"
                  : "No appointments found"}
              </p>
              {view === "search" &&
                (searchParams.userName || searchParams.userPhone) && (
                  <p className="text-sm text-gray-400">
                    Try adjusting your search parameters
                  </p>
                )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 bg-gray-50">
          <Button variant="outline" onClick={onClose} className="w-full">
            Cancel
          </Button>
        </div>
      </div>
    </>
  );
};

// Appointment Card Component (unchanged)
const AppointmentCard = ({
  appointment,
  onSelect,
  formatDateTime,
  getStatusColor,
}) => {
  return (
    <div
      onClick={onSelect}
      className="p-4 border border-gray-200 rounded-lg hover:border-primary hover:bg-primary/5 cursor-pointer transition-all"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-medium text-gray-900">
              {appointment.userName}
            </span>
            <Badge variant={getStatusColor(appointment.status)} size="sm">
              {appointment.status}
            </Badge>
          </div>
          <div className="text-sm text-gray-600 font-mono">
            {appointment.appointmentCode}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 text-sm">
        <div className="flex items-center gap-2 text-gray-600">
          <FiPhone className="text-gray-400" size={14} />
          {appointment.userPhone}
        </div>
        <div className="flex items-center gap-2 text-gray-600">
          <FiClock className="text-gray-400" size={14} />
          {formatDateTime(appointment.scheduledDateTime)}
        </div>
      </div>

      <div className="mt-3 pt-3 border-t border-gray-100">
        <div className="flex items-center justify-between text-xs">
          <span className="text-gray-500">
            Type:{" "}
            <span className="font-medium text-gray-700">
              {appointment.type?.replace(/_/g, " ")}
            </span>
          </span>
          <span className="text-gray-500">
            Duration:{" "}
            <span className="font-medium text-gray-700">
              {appointment.duration} min
            </span>
          </span>
        </div>
      </div>
    </div>
  );
};

export default StartSessionModal;
